import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, GraduationCap, Trash2, ChevronRight, NotebookPen, Brain, Medal, Loader2, CheckCircle2, AlertCircle, Layers } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import TopBar from "../components/TopBar";

export default function Dashboard() {
  const { user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [exams, setExams] = useState(null);
  const [reviewDue, setReviewDue] = useState(0);

  const load = () => api.get("/exams").then((r) => setExams(r.data.exams)).catch(() => setExams([]));
  useEffect(() => {
    load();
    api.get("/review").then((r) => setReviewDue(r.data.due_count)).catch(() => {});
  }, []);

  // poll while any exam is generating
  useEffect(() => {
    if (!exams) return;
    if (exams.some((e) => e.status === "generating")) {
      const t = setTimeout(load, 5000);
      return () => clearTimeout(t);
    }
  }, [exams]);

  const del = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/exams/${id}`); toast.success(t("Esame eliminato")); load(); } catch { toast.error(t("Errore")); }
  };

  if (!exams) return <div className="space-bg min-h-screen flex items-center justify-center text-white/60">{t("Caricamento...")}</div>;

  const statusChip = (ex) => {
    if (ex.status === "generating") {
      const p = ex.progress || {};
      return <span className="flex items-center gap-1 text-xs font-num" style={{ color: "var(--cyan)" }}><Loader2 size={13} className="animate-spin" /> {p.total ? `${p.done}/${p.total} ${t("argomenti")}` : t("Analisi appunti...")}</span>;
    }
    if (ex.status === "error") return <span className="flex items-center gap-1 text-xs" style={{ color: "var(--danger)" }}><AlertCircle size={13} /> {t("Errore")}</span>;
    return <span className="text-xs text-white/40 font-num">{ex.topic_count} {t("argomenti")} · {ex.completed_levels}/{ex.total_levels} {t("livelli")}</span>;
  };

  return (
    <div className="space-bg min-h-screen pb-16">
      <TopBar />
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
            <GraduationCap style={{ color: "var(--success)" }} /> {t("I tuoi Esami")}
          </h1>
          <p className="text-white/60">{t("Carica gli appunti: l'AI li divide in argomenti e costruisce i livelli.")}</p>
        </motion.div>

        <button onClick={() => navigate("/exam/new")} className="w-full glass rounded-3xl p-5 mb-6 flex items-center gap-4 text-left bouncy relative overflow-hidden" data-testid="create-exam-btn">
          <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full opacity-20" style={{ background: "var(--success)" }} />
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(0,255,127,0.15)", boxShadow: "0 0 18px rgba(0,255,127,0.4)" }}>
            <Plus size={28} style={{ color: "var(--success)" }} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg tracking-tight">{t("Crea nuovo esame")}</h3>
            <p className="text-xs text-white/60">{t("Nome + libro + appunti/foto → livelli e flashcard automatici")}</p>
          </div>
          <ChevronRight size={20} className="text-white/40" />
        </button>

        {exams.length === 0 ? (
          <p className="text-center text-white/50 my-10" data-testid="no-exams-msg">{t("Nessun esame ancora. Creane uno dai tuoi appunti! 🎓📸")}</p>
        ) : (
          <div className="space-y-3 mb-8">
            {exams.map((ex, i) => (
              <motion.button key={ex.exam_id} onClick={() => navigate(`/exam/${ex.exam_id}`)}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="w-full glass rounded-2xl p-4 text-left bouncy flex items-center gap-3" data-testid={`exam-item-${ex.exam_id}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(0,255,127,0.12)" }}>
                  {ex.status === "ready" ? <CheckCircle2 size={20} style={{ color: "var(--success)" }} /> : ex.status === "error" ? <AlertCircle size={20} style={{ color: "var(--danger)" }} /> : <Loader2 size={20} className="animate-spin" style={{ color: "var(--cyan)" }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold tracking-tight truncate">{ex.name}</h3>
                  {statusChip(ex)}
                </div>
                <Trash2 size={18} className="text-white/40 hover:text-red-400 shrink-0" onClick={(e) => del(e, ex.exam_id)} data-testid={`exam-delete-${ex.exam_id}`} />
                <ChevronRight size={18} className="text-white/40 shrink-0" />
              </motion.button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-4 gap-3">
          <button onClick={() => navigate("/progress")} className="glass rounded-2xl p-4 bouncy flex flex-col items-center gap-2 text-center" data-testid="nav-progress">
            <Layers size={22} style={{ color: "var(--cyan)" }} />
            <span className="font-heading font-bold text-xs">{t("Progressi")}</span>
          </button>
          <button onClick={() => navigate("/plans")} className="glass rounded-2xl p-4 bouncy flex flex-col items-center gap-2 text-center" data-testid="nav-plans">
            <NotebookPen size={22} style={{ color: "var(--violet)" }} />
            <span className="font-heading font-bold text-xs">{t("Piano")}</span>
          </button>
          <button onClick={() => navigate("/review")} className="glass rounded-2xl p-4 bouncy flex flex-col items-center gap-2 text-center relative" data-testid="nav-review">
            {reviewDue > 0 && <span className="absolute top-2 right-2 w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-num font-bold" style={{ background: "var(--pink)" }} data-testid="review-badge-count">{reviewDue}</span>}
            <Brain size={22} style={{ color: "var(--pink)" }} />
            <span className="font-heading font-bold text-xs">{t("Ripasso")}</span>
          </button>
          <button onClick={() => navigate("/badges")} className="glass rounded-2xl p-4 bouncy flex flex-col items-center gap-2 text-center" data-testid="nav-badges">
            <Medal size={22} style={{ color: "var(--gold)" }} />
            <span className="font-heading font-bold text-xs">{t("Traguardi")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
